import { useCallback, useEffect, useState } from 'react';
import { tenantAPI } from '../api/tenant.js';
import { isPlatformAdmin } from '../utils/permissions.js';
import {
  getPlatformViewTenantId,
  resolveDataTenantId,
  setPlatformViewTenantId,
  PLATFORM_VIEW_TENANT_EVENT
} from '../utils/platformViewTenant.js';
import { invalidatePlantProtectionStats } from '../utils/plantProtectionStatsCache.js';

function normalizeTenantList(res) {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.list)) return body.list;
  return [];
}

export default function usePlatformViewTenant() {
  const platformAdmin = isPlatformAdmin();
  const [viewTenantId, setViewTenantIdState] = useState(() => getPlatformViewTenantId() || '');
  const [dataTenantId, setDataTenantId] = useState(() => resolveDataTenantId());
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!platformAdmin) return undefined;
    let cancelled = false;
    setLoading(true);
    tenantAPI
      .getAccessible()
      .then((res) => {
        if (cancelled) return;
        const list = normalizeTenantList(res);
        if (list.length > 0) {
          setTenants(list);
          return;
        }
        return tenantAPI.getAll().then((allRes) => {
          if (!cancelled) setTenants(normalizeTenantList(allRes));
        });
      })
      .catch(() =>
        tenantAPI.getAll().then((allRes) => {
          if (!cancelled) setTenants(normalizeTenantList(allRes));
        })
      )
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [platformAdmin]);

  const syncFromStorage = useCallback(() => {
    setViewTenantIdState(getPlatformViewTenantId() || '');
    setDataTenantId(resolveDataTenantId());
  }, []);

  useEffect(() => {
    const handler = () => syncFromStorage();
    window.addEventListener(PLATFORM_VIEW_TENANT_EVENT, handler);
    return () => window.removeEventListener(PLATFORM_VIEW_TENANT_EVENT, handler);
  }, [syncFromStorage]);

  const setViewTenantId = useCallback((tenantId) => {
    setPlatformViewTenantId(tenantId || null);
    setViewTenantIdState(tenantId || '');
    setDataTenantId(resolveDataTenantId());
    invalidatePlantProtectionStats();
  }, []);

  return {
    platformAdmin,
    tenants,
    viewTenantId,
    dataTenantId,
    setViewTenantId,
    loading
  };
}
